import React, { useEffect } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeftIcon, ArrowRightIcon, MailIcon, InfinityIcon, TruckIcon, RotateCcwIcon, ShieldCheckIcon } from 'lucide-react';
import { getLegalDoc, legalDocs, LegalSection } from '../data/legal';
import { media } from '../data/product';
import { SiteFooter } from '../components/SiteFooter';
import { PaymentLogos } from '../components/PaymentLogos';
const promises = [{
  icon: InfinityIcon,
  label: 'Break it. We replace it.'
}, {
  icon: RotateCcwIcon,
  label: '30-day returns'
}, {
  icon: TruckIcon,
  label: 'Ships from the U.S. in 1 business day'
}, {
  icon: ShieldCheckIcon,
  label: 'Real person on support'
}];
export function Legal() {
  const {
    slug = ''
  } = useParams();
  const doc = getLegalDoc(slug);
  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);
  const renderSection = (section: LegalSection) => <section key={section.heading} className="border-t border-white/10 pt-7" data-id="element-1873" data-mp="3s7l0d24na3pr">
      <h2 className={section.fine ? 'font-display text-lg font-bold uppercase tracking-wide text-steel-400' : 'font-display text-2xl font-extrabold uppercase leading-tight text-offwhite sm:text-3xl'} data-id="element-1874" data-mp="27kv0ljspujb3">
        {section.heading}
      </h2>
      <div className={`mt-3 grid max-w-3xl ${section.fine ? 'gap-2' : 'gap-3'}`} data-id="element-1875" data-mp="2ckhdtt8jnzjw">
        {section.body.map(paragraph => <p key={paragraph} className={section.fine ? 'text-[12px] leading-relaxed text-steel-500' : 'text-[15px] leading-relaxed text-steel-300 sm:text-base'} data-id="element-1876" data-mp="1ovonqdelslx7">
            {paragraph}
          </p>)}
      </div>
    </section>;
  return <div className="min-h-full w-full bg-ink-950 font-sans text-offwhite" data-id="element-1877" data-mp="1trwyzfpojuz3">
      <header className="sticky top-0 z-40 border-b border-white/10 bg-ink-950/95 backdrop-blur" data-id="element-1878" data-mp="162kswopng310">
        <div className="mx-auto flex max-w-[1400px] items-center justify-between gap-4 px-4 py-3 sm:px-6 lg:px-8" data-id="element-1879" data-mp="114j89zckrvnf">
          <Link to="/" className="shrink-0" data-id="element-1880" data-mp="3157ohzl0sj9k">
            <span className="block font-display text-2xl font-extrabold uppercase leading-none tracking-tight text-offwhite sm:text-3xl" data-id="element-1881" data-mp="365lnyt8k2kvz">
              Grit<span className="text-grit-500" data-id="element-1882" data-mp="3axwxmnt3ll1q">Vue</span>
              <sup className="ml-0.5 align-super text-[9px] text-steel-400" data-id="element-1883" data-mp="3eyynwf51emrk">™</sup>
            </span>
            <span className="mt-0.5 block text-[8px] font-semibold uppercase tracking-[0.3em] text-steel-400 sm:text-[9px]" data-id="element-1884" data-mp="2i97wios1lfpz">
              See the job through
            </span>
          </Link>
          <Link to="/#offer" className="inline-flex shrink-0 items-center bg-grit-500 px-3.5 py-2.5 font-display text-base font-extrabold uppercase leading-none tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] sm:px-4 sm:text-lg" data-id="element-1885" data-mp="2r8mhj8pfwy33">
            Get your X1
          </Link>
        </div>
      </header>

      <main className="mx-auto max-w-[1400px] px-4 py-10 sm:px-6 lg:px-8 lg:py-16" data-id="element-1886" data-mp="lkvn7397amge">
        <Link to="/" className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.16em] text-steel-400 transition-colors duration-150 ease-grit hover:text-grit-500" data-id="element-1887" data-mp="gmkbso39c3w2">
          <ArrowLeftIcon className="h-4 w-4" aria-hidden="true" data-id="element-1888" data-mp="7tqrgc8yeebc" />
          Back to the X1
        </Link>

        {!doc ? <div className="mt-8" data-id="element-1889" data-mp="2tcimrzj30c9">
            <h1 className="grit-headline text-[clamp(2.25rem,8vw,4rem)] font-extrabold uppercase text-offwhite" data-id="element-1890" data-mp="3u202t4m18hi8">
              Page not found
            </h1>
            <p className="mt-4 text-lg text-steel-300" data-id="element-1891" data-mp="3p2x9ghfezzq3">Pick one of our policies below.</p>
          </div> : <div className="mt-8 grid gap-12 lg:grid-cols-[1fr_320px] lg:gap-16" data-id="element-1892" data-mp="3l3er9teza2qy">
            <article data-id="element-1893" data-mp="1nahflpzopzgr">
              <p className="text-[11px] font-bold uppercase tracking-[0.3em] text-grit-500" data-id="element-1894" data-mp="1j9p7x16go2ml">{doc.eyebrow}</p>
              <h1 className="grit-headline mt-3 text-[clamp(2.25rem,8vw,4.5rem)] font-extrabold uppercase text-offwhite" data-id="element-1895" data-mp="1qd550hd7t3lc">
                {doc.title}
              </h1>
              <p className="mt-2 text-xs font-semibold uppercase tracking-wide text-steel-500" data-id="element-1896" data-mp="1v9vwe0ugzlm2">
                Last updated {doc.updated}
              </p>
              <p className="mt-6 max-w-3xl text-lg leading-relaxed text-steel-200" data-id="element-1897" data-mp="1ld0wpemgnf67">{doc.intro}</p>

              <div className="mt-10 grid gap-8" data-id="element-1898" data-mp="17m4bi8uv6096">
                {doc.sections.filter(section => !section.fine).map(renderSection)}
              </div>

              <div className="mt-12 grid gap-px border-2 border-grit-500 bg-white/10 lg:grid-cols-[1.1fr_1fr]" data-id="element-1899" data-mp="1cexfguiid4k7">
                <img src={media.lensSheet} alt="GritVue X1 illuminated work glasses shown with its clear, amber and smoke lenses" className="block h-auto w-full bg-ink-950 object-contain" data-id="element-1900" data-mp="12nawgbzxi16w" />
                <div className="flex flex-col justify-center bg-ink-950 p-6 lg:p-8" data-id="element-1901" data-mp="tltgquqfo1iq">
                  <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-grit-500" data-id="element-1902" data-mp="a1m8p3m2tb4v">
                    The complete system
                  </p>
                  <p className="grit-headline mt-3 text-[clamp(1.75rem,4vw,2.75rem)] font-extrabold uppercase text-offwhite" data-id="element-1903" data-mp="j197dhnf82u8">
                    Still want yours?
                    <br data-id="element-1904" data-mp="f20j2sb5gx62" />
                    <span className="text-grit-500" data-id="element-1905" data-mp="szfbm5wp9l29">Pick your pack.</span>
                  </p>
                  <p className="mt-3 text-[15px] leading-relaxed text-steel-300" data-id="element-1906" data-mp="o0lvs6txxhyx">
                    Build your X1 from $29.95, or get the Complete System: 3 lenses, charging cable, hard case AV8, pouch,
                    cleaning cloth and Quick Start Guide, with the Lifetime Replacement Warranty free on every multi-pack.
                  </p>
                  <Link to="/#offer" className="mt-6 inline-flex items-center justify-center gap-2 bg-grit-500 px-6 py-4 text-center font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px] sm:text-2xl" data-id="element-1907" data-mp="1bqy9xzfs4axy">
                    Back to the packs
                    <ArrowRightIcon className="h-5 w-5" aria-hidden="true" data-id="element-1908" data-mp="dc99x2l1li43" />
                  </Link>
                  <p className="mt-3 text-xs font-semibold uppercase tracking-wide text-steel-400" data-id="element-1909" data-mp="i76u95nrvdwa">
                    See current packs and pricing
                  </p>
                </div>
              </div>

              {doc.sections.some(section => section.fine) && <div className="mt-10 grid gap-8" data-id="element-1910" data-mp="x2us8d2hz4rq">
                  {doc.sections.filter(section => section.fine).map(renderSection)}
                </div>}

              <div className="mt-8 flex flex-col gap-4 border border-white/12 bg-ink-900 p-6 sm:flex-row sm:items-center sm:justify-between" data-id="element-1911" data-mp="2yurg8m5uq73e">
                <p data-id="element-1912" data-mp="32xe1u5bxivs5">
                  <span className="block font-display text-2xl font-extrabold uppercase leading-none text-grit-500" data-id="element-1913" data-mp="2vbsdm7syp2u9">
                    Still have a question?
                  </span>
                  <a href="mailto:support@gritvuegear.com" className="mt-2 inline-flex items-center gap-2 text-[15px] font-bold uppercase tracking-wide text-offwhite transition-colors duration-150 ease-grit hover:text-grit-500" data-id="element-1914" data-mp="2qj8vqyn8rljn">
                    <MailIcon className="h-4 w-4 text-grit-500" aria-hidden="true" data-id="element-1915" data-mp="3e8k0ssbmk23l" />
                    support@gritvuegear.com
                  </a>
                </p>
                <Link to="/#offer" className="inline-flex items-center justify-center bg-grit-500 px-6 py-4 font-display text-xl font-extrabold uppercase tracking-wide text-ink-950 transition-[background-color,transform] duration-150 ease-grit hover:bg-grit-400 active:translate-y-[2px]" data-id="element-1916" data-mp="358n6aa2k090m">
                  Shop the X1
                </Link>
              </div>
            </article>

            <aside className="lg:sticky lg:top-24 lg:self-start" data-id="element-1917" data-mp="3p0c2g9iqiv78">
              <nav aria-label="Policies" className="border border-white/12 bg-ink-900" data-id="element-1918" data-mp="3t0cjm1x7xkhz">
                <p className="border-b border-white/10 px-5 py-3.5 text-[11px] font-bold uppercase tracking-[0.2em] text-steel-400" data-id="element-1919" data-mp="3f0ti3rywx7en">
                  Policies
                </p>
                <ul data-id="element-1920" data-mp="bit8k8dpktyx">
                  {legalDocs.map(item => <li key={item.slug} className="border-b border-white/5 last:border-b-0" data-id="element-1921" data-mp="gi66ppa15ngt">
                      <Link to={`/legal/${item.slug}`} className={`block px-5 py-3.5 font-display text-lg font-bold uppercase tracking-wide transition-colors duration-150 ease-grit ${item.slug === doc.slug ? 'bg-grit-500/10 text-grit-500' : 'text-steel-300 hover:text-offwhite'}`} data-id="element-1922" data-mp="gjz1o7ba6j8z">
                        {item.title}
                      </Link>
                    </li>)}
                </ul>
              </nav>

              <ul className="mt-6 grid gap-3 border border-white/12 bg-ink-900 p-5" data-id="element-1923" data-mp="2srczm9mah21k">
                {promises.map(promise => {
              const Icon = promise.icon;
              return <li key={promise.label} className="flex items-start gap-3 text-[14px] font-medium text-steel-200" data-id="element-1924" data-mp="20zx4fr6o26zq">
                      <Icon className="mt-0.5 h-5 w-5 shrink-0 text-grit-500" aria-hidden="true" data-id="element-1925" data-mp="1w1vovg8g8pt0" />
                      {promise.label}
                    </li>;
            })}
              </ul>

              <div className="mt-6" data-id="element-1926" data-mp="2510m8j89yd3l">
                <p className="text-[10px] font-bold uppercase tracking-[0.24em] text-steel-500" data-id="element-1927" data-mp="2544x1zg8w738">Secure checkout</p>
                <div className="mt-2" data-id="element-1928" data-mp="3ggyzz0eednb5">
                  <PaymentLogos data-id="element-1929" data-mp="3gfxf647kxmg8" />
                </div>
              </div>
            </aside>
          </div>}
      </main>

      <SiteFooter data-id="element-1930" data-mp="179fxzqixzzod" />
    </div>;
}